import React, { useState, useEffect } from 'react';
import {
  Plus,
  Calendar,
  DollarSign,
  Landmark,
  PiggyBank,
  ArrowDownRight,
  ArrowUpRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { accountsService } from '../../services/accountsService';
import { toast } from 'react-toastify';

const CashTransactions = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    cashAccountId: '',
    txnDate: new Date().toISOString().split('T')[0],
    type: 'credit',
    amount: '',
    narration: ''
  });
  const [filters, setFilters] = useState({ cashAccountId: '', page: 1 });

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadData();
  }, [filters.page, filters.cashAccountId]);

  const loadAccounts = async () => {
    try {
      const response = await accountsService.getCashAccounts();
      if (response?.success && response.data?.length > 0) {
        setAccounts(response.data);
        if (!formData.cashAccountId) {
          setFormData(f => ({ ...f, cashAccountId: response.data[0]._id }));
        }
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, limit: 20 };
      if (filters.cashAccountId) params.cashAccountId = filters.cashAccountId;
      const response = await accountsService.getCashTransactions(params);
      if (response?.success) {
        const transformed = (response.data || []).map(item => ({
          ...item,
          id: item._id
        }));
        setData(transformed);
      } else {
        toast.error('Failed to load transactions');
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      cashAccountId: accounts[0]?._id || '',
      txnDate: new Date().toISOString().split('T')[0],
      type: 'credit',
      amount: '',
      narration: ''
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.cashAccountId) {
      toast.error('Please select an account');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const response = await accountsService.createCashTransaction({
        cashAccountId: formData.cashAccountId,
        txnDate: formData.txnDate,
        type: formData.type,
        amount,
        narration: formData.narration || ''
      });
      if (response?.success) {
        toast.success('Transaction added successfully');
        setShowForm(false);
        loadData();
      } else {
        toast.error(response?.message || 'Failed to add transaction');
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error(error?.response?.data?.message || 'Failed to add transaction');
    }
  };

  const formatCurrency = (amount, currency = 'INR') =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount || 0);

  const columns = [
    {
      accessorKey: 'txnDate',
      header: 'Date',
      cell: ({ getValue }) => (
        <div className="d-flex align-items-center gap-2">
          <Calendar size={14} className="text-muted" />
          {new Date(getValue()).toLocaleDateString()}
        </div>
      )
    },
    {
      accessorKey: 'cashAccountId',
      header: 'Account',
      cell: ({ row }) => {
        const acc = row.original.cashAccountId;
        const name = typeof acc === 'object' ? acc?.name : '-';
        const type = typeof acc === 'object' ? acc?.type : null;
        return (
          <div className="d-flex align-items-center gap-2">
            {type === 'bank' ? (
              <Landmark size={14} className="text-primary" />
            ) : (
              <PiggyBank size={14} className="text-success" />
            )}
            {name}
          </div>
        );
      }
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }) =>
        getValue() === 'credit' ? (
          <span className="badge bg-success">
            <ArrowUpRight size={12} /> Credit
          </span>
        ) : (
          <span className="badge bg-danger">
            <ArrowDownRight size={12} /> Debit
          </span>
        )
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue, row }) => {
        const acc = row.original.cashAccountId;
        const currency = typeof acc === 'object' ? acc?.currency : 'INR';
        const type = row.original.type;
        const amt = getValue();
        return (
          <span className={type === 'credit' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
            {type === 'credit' ? '+' : '-'} {formatCurrency(amt, currency)}
          </span>
        );
      }
    },
    {
      accessorKey: 'narration',
      header: 'Narration',
      cell: ({ getValue }) => (
        <span className="text-muted" style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {getValue() || '-'}
        </span>
      )
    }
  ];

  const breadcrumbItems = [
    { label: 'Accounts', path: '/accounts', onClick: () => navigate('/accounts') },
    { label: 'Transactions', path: '/accounts?tab=transactions', onClick: () => navigate('/accounts?tab=transactions') },
    { label: 'Cash Transactions', path: '/accounts/cash-transactions', isCurrent: true }
  ];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Cash Transactions</h1>
          <p>Record debits and credits for cash and bank accounts</p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={accounts.length === 0}
            title={accounts.length === 0 ? 'Create cash accounts first' : ''}
          >
            <Plus size={16} />
            Add Transaction
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 d-flex gap-2 align-items-center">
        <label className="form-label mb-0">Account:</label>
        <select
          className="form-select"
          style={{ width: 'auto', minWidth: 200 }}
          value={filters.cashAccountId}
          onChange={(e) => setFilters(f => ({ ...f, cashAccountId: e.target.value, page: 1 }))}
        >
          <option value="">All Accounts</option>
          {accounts.map((acc) => (
            <option key={acc._id} value={acc._id}>
              {acc.name} ({acc.type})
            </option>
          ))}
        </select>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          noCard={true}
          className="cash-transactions-table"
          pageSize={20}
        />
      </div>

      {/* Add Transaction Modal */}
      {showForm && (
        <div
          className="confirmation-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
        >
          <div className="confirmation-modal" style={{ maxWidth: '480px' }}>
            <div className="confirmation-modal-header">
              <h3 className="confirmation-modal-title mb-0">Add Cash Transaction</h3>
              <button
                className="confirmation-modal-close"
                onClick={() => setShowForm(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="confirmation-modal-content" style={{ padding: '1.5rem' }}>
              <div className="mb-3">
                <label className="form-label">Account *</label>
                <select
                  className="form-select"
                  value={formData.cashAccountId}
                  onChange={(e) => setFormData(f => ({ ...f, cashAccountId: e.target.value }))}
                >
                  <option value="">Select account</option>
                  {accounts.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.name} ({acc.type})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.txnDate}
                  onChange={(e) => setFormData(f => ({ ...f, txnDate: e.target.value }))}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={formData.type}
                  onChange={(e) => setFormData(f => ({ ...f, type: e.target.value }))}
                >
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="form-control"
                  value={formData.amount}
                  onChange={(e) => setFormData(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Narration</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={formData.narration}
                  onChange={(e) => setFormData(f => ({ ...f, narration: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="confirmation-modal-footer" style={{ padding: '1rem 1.5rem' }}>
              <button className="confirmation-modal-cancel" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSubmit}>
                Save Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashTransactions;
